// frontend/src/components/asset/ReturnToLessorModal.js
import React, { useEffect, useState } from 'react';
import { Building2, Undo2 } from 'lucide-react';
import toast from '../../utils/toast';
import { canReturnToLessor } from '../../utils/assetAccess';
import {
  getTodayInSingaporeTimezone,
  parseLocalDate,
  toLocalISODate,
} from '../../utils/dateUtils';
import Modal from '../UI/Modal';
import DatePickerField from '../UI/DatePickerField';

/**
 * Custodian confirmation for handing a leased asset back to its lessor.
 *
 * Distinct from Return to Inventory (the asset stays in our stock) and from
 * disposal (we owned it and sold, scrapped, or wrote it off).
 */
const ReturnToLessorModal = ({ isOpen, onClose, asset, onSubmit, isLoading }) => {
  const [returnDate, setReturnDate] = useState(getTodayInSingaporeTimezone());
  const [returnedTo, setReturnedTo] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!isOpen || !asset) return;
    setReturnDate(getTodayInSingaporeTimezone());
    setReturnedTo(asset.vendor_name || asset.purchase_vendor || '');
    setNotes('');
  }, [isOpen, asset]);

  if (!asset) return null;

  const holder = asset.current_assignee_name
    ? `${asset.current_assignee_name}${asset.current_assignee_staff_id ? ` (${asset.current_assignee_staff_id})` : ''}`
    : null;

  const handleSubmit = (e) => {
    e.preventDefault();
    const lessor = returnedTo.trim();
    if (!returnDate) {
      toast.error('Date returned is required');
      return;
    }
    if (returnDate > getTodayInSingaporeTimezone()) {
      toast.error('Date returned cannot be in the future');
      return;
    }
    if (!lessor) {
      toast.error('Enter who the asset was returned to');
      return;
    }
    onSubmit({
      return_date: returnDate,
      returned_to: lessor,
      notes: notes.trim(),
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Return to Lessor"
      size="md"
      theme="admin"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="btn btn-admin-outline"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="return-to-lessor-form"
            disabled={isLoading}
            className="btn btn-admin-primary"
          >
            <Undo2 className="w-4 h-4 mr-2" />
            {isLoading ? 'Recording…' : 'Record return'}
          </button>
        </>
      }
    >
      <form id="return-to-lessor-form" onSubmit={handleSubmit} className="space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
            <Building2 className="w-5 h-5 text-slate-600" />
          </div>
          <div className="min-w-0">
            <p className="text-sm text-gray-700">
              Hand <span className="font-medium">{asset.asset_tag}</span> back to the lessor.
              {asset.brand || asset.model ? (
                <> { [asset.brand, asset.model].filter(Boolean).join(' ') }.</>
              ) : null}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {holder ? `Currently with ${holder}.` : 'Currently in our inventory, not assigned.'}
              {asset.serial_number ? ` Serial ${asset.serial_number}.` : ''}
            </p>
          </div>
        </div>

        <div className="rounded-lg bg-gray-50 p-3 text-sm text-gray-600 space-y-1">
          <p className="font-medium text-gray-700">This will:</p>
          <ul className="list-disc list-inside space-y-0.5">
            <li>Set the status to <span className="font-medium">Returned to Lessor</span></li>
            <li>Record the hand-back date and who received it</li>
            <li>Take it out of active inventory and off anyone&apos;s My Assets</li>
            <li>Keep the record. Search the tag or serial, or filter status to Returned to Lessor</li>
          </ul>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="lease-return-date">
            Date returned <span className="text-red-500">*</span>
          </label>
          <DatePickerField
            id="lease-return-date"
            theme="admin"
            selected={parseLocalDate(returnDate)}
            onChange={(date) => setReturnDate(date ? toLocalISODate(date) : '')}
            maxDate={parseLocalDate(getTodayInSingaporeTimezone())}
            placeholderText="Select date"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="lease-returned-to">
            Returned to <span className="text-red-500">*</span>
          </label>
          <input
            id="lease-returned-to"
            type="text"
            value={returnedTo}
            onChange={(e) => setReturnedTo(e.target.value)}
            className="input-admin w-full"
            placeholder="e.g. EXEO Global"
            maxLength={200}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="lease-return-notes">
            Notes
          </label>
          <textarea
            id="lease-return-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="input-admin w-full h-20 resize-none"
            placeholder="Acknowledgement reference, accessories included, or who collected it"
            maxLength={2000}
          />
        </div>
      </form>
    </Modal>
  );
};

export const ReturnToLessorButton = ({ asset, onClick, iconClassName = 'w-4 h-4' }) => {
  if (!canReturnToLessor(asset)) return null;
  return (
    <button
      type="button"
      onClick={() => onClick(asset)}
      className="icon-button-admin text-slate-600 hover:text-slate-800 hover:bg-slate-50"
      title="Return to lessor"
      aria-label="Return to lessor"
    >
      <Undo2 className={iconClassName} />
    </button>
  );
};

export default ReturnToLessorModal;
